import React, { ReactNode } from 'react';
import QRCode from 'qrcode.react';
import { FormattedMessage } from 'react-intl';
import { PageView } from '../../../../../../../models/analytics';
import { ProviderEntity } from '../../../../../../../models/provider';
import { BankIdLogo } from '../../../../../../atoms/BankIdLogo/BankIdLogo';
import { usePageViewEvents } from '../../../../../../../core/Analytics/usePageViewEvents';
import { BackButton } from '../../../../../../organisms/Header/HeaderMolecule';
import { AwaitingQrCodeView } from '../../../../atoms/AwaitingQrCodeView';
import { BoldText } from '../../../../../../../utilities/richTextUtils';
import { messages } from './messages';

export const buildBankIdAutoStartUrl = (autoStartToken: string) => {
  return `https://app.bankid.com/?autostarttoken=${autoStartToken}&redirect=null`;
};

export enum BankIdAuthenticationType {
  BANK_ID_AUTO_START_TOKEN = 'BANK_ID_AUTO_START_TOKEN',
  BANK_ID_ANIMATED_QR_CODE = 'BANK_ID_ANIMATED_QR_CODE',
}

export type BankIdAuthWithSupplementalContainerProps = {
  provider: ProviderEntity;
  supplementalInformation: {
    data: string;
    type: BankIdAuthenticationType;
  };
  isPay: boolean;
  heading: ReactNode;
  footerDisclaimer: React.ReactNode | undefined;
} & BackButton;

export const BankIdAuthWithSupplementalContainer: React.FC<
  BankIdAuthWithSupplementalContainerProps
> = ({
  provider,
  supplementalInformation,
  isPay,
  heading,
  footerDisclaimer,
  onBack,
}: BankIdAuthWithSupplementalContainerProps) => {
  usePageViewEvents({ pageView: PageView.BANKID_REMOTE_AWAITING_SCREEN });

  const qrPayload =
    supplementalInformation.type === BankIdAuthenticationType.BANK_ID_ANIMATED_QR_CODE
      ? supplementalInformation.data
      : buildBankIdAutoStartUrl(supplementalInformation.data);

  return (
    <AwaitingQrCodeView
      onBack={onBack}
      heading={heading}
      provider={provider}
      allowExit={!isPay}
      icon={<BankIdLogo size="40px" />}
      instructions={<FormattedMessage {...messages.bankIdInstructions} values={{ b: BoldText }} />}
      view={
        supplementalInformation.type === BankIdAuthenticationType.BANK_ID_ANIMATED_QR_CODE
          ? PageView.ANIMATED_QR_CODE_SCREEN
          : PageView.BANKID_REMOTE_AWAITING_SCREEN
      }
      qrCode={
        <QRCode
          data-testid={
            supplementalInformation.type === BankIdAuthenticationType.BANK_ID_ANIMATED_QR_CODE
              ? 'animated-qr-code'
              : 'qr-code'
          }
          value={qrPayload}
          size={220}
          includeMargin
        />
      }
      paymentDetails={footerDisclaimer}
    />
  );
};
